import type { NoteName } from "../guitarEngine";
import type { ExplorePlaybackRange } from "../explore3nps";

export interface ScalePathMission {
  id: string;
  order: number;
  title: string;
  subtitle: string;
  description: string;
  instructions: string[];
  tags: string[];
  timeEstimateMin: number;
  root: NoteName;
  scaleType: string;
  /** Índice en selectCanonical3NpsFingerings (0 = POS I). */
  positionIndex: number;
  playbackRange: ExplorePlaybackRange;
  maxFret: number;
  /** Ida y vuelta sobre la secuencia generada. */
  direction: "asc" | "both";
  bpm: number;
  unlockRequires?: string[];
}

export const SCALE_PATH_MISSIONS: ScalePathMission[] = [
  {
    id: "path-l1-pos1-octave",
    order: 1,
    title: "Nivel 1 — Primera posición",
    subtitle: "Do mayor · una octava (POS I)",
    description:
      "Tu primera escala en caja: posición canónica I en Do mayor. Escucha el recorrido y luego repítelo con el micrófono, nota a nota.",
    instructions: [
      "Observa la caja resaltada en el mástil (POS I).",
      "En Aprender, escucha la subida de una octava desde la tónica.",
      "En Practicar, toca cada nota cuando se ilumine.",
    ],
    tags: ["POS I", "Mayor", "5 trastes"],
    timeEstimateMin: 6,
    root: "C",
    scaleType: "major",
    positionIndex: 0,
    playbackRange: "octave",
    maxFret: 5,
    direction: "asc",
    bpm: 56,
  },
  {
    id: "path-l2-pos1-extend",
    order: 2,
    title: "Nivel 2 — Ampliando la caja",
    subtitle: "Sol mayor · POS I extendida",
    description:
      "La misma geometría POS I en Sol mayor, recorriendo más notas de la caja hasta el traste 8.",
    instructions: [
      "Compara la forma con Do mayor: la caja se desplaza pero la geometría es la misma.",
      "Aprender: escucha el recorrido extendido.",
      "Practicar: valida cada nota con la guitarra.",
    ],
    tags: ["POS I", "Mayor", "8 trastes"],
    timeEstimateMin: 7,
    root: "G",
    scaleType: "major",
    positionIndex: 0,
    playbackRange: "extend",
    maxFret: 8,
    direction: "asc",
    bpm: 60,
    unlockRequires: ["path-l1-pos1-octave"],
  },
  {
    id: "path-l3-pos1-both",
    order: 3,
    title: "Nivel 3 — Ida y vuelta",
    subtitle: "Re mayor · subida y bajada",
    description:
      "Recorrido completo en POS I: subes y bajas la escala en Re mayor. Prepara el terreno para explorar otras posiciones.",
    instructions: [
      "Escucha la ida y la vuelta en Aprender.",
      "En Practicar, mantén el ritmo al descender.",
      "Completa la misión cuando termines todas las notas.",
    ],
    tags: ["POS I", "Mayor", "Ida y vuelta"],
    timeEstimateMin: 8,
    root: "D",
    scaleType: "major",
    positionIndex: 0,
    playbackRange: "extend",
    maxFret: 12,
    direction: "both",
    bpm: 64,
    unlockRequires: ["path-l2-pos1-extend"],
  },
];

export function getScalePathMissionById(id: string): ScalePathMission | undefined {
  return SCALE_PATH_MISSIONS.find((m) => m.id === id);
}

export function isScalePathMissionUnlocked(
  mission: ScalePathMission,
  completedIds: string[]
): boolean {
  if (!mission.unlockRequires?.length) return true;
  return mission.unlockRequires.every((id) => completedIds.includes(id));
}

export function getFirstUnlockedScalePathMission(
  completedIds: string[]
): ScalePathMission | undefined {
  return SCALE_PATH_MISSIONS.find((m) => isScalePathMissionUnlocked(m, completedIds));
}
